// Cash payment section — inline panel state, migrated from payment-cash-dialog.tsx
import { useState, useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtVND = (n: number) => new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
const QUICK_AMOUNTS = [50000, 100000, 200000, 500000, 1000000];

interface PaymentCashSectionProps {
  total: number;
  hasCustomer: boolean;
  couponCode?: string;
  onConfirm: (tenderedAmount: number, couponCode?: string, reason?: string) => void;
  onBack: () => void;
  loading?: boolean;
}

export function PaymentCashSection({ total, hasCustomer, couponCode, onConfirm, onBack, loading }: PaymentCashSectionProps) {
  const [tendered, setTendered] = useState("");
  const [allowPartial, setAllowPartial] = useState(false);
  const [reason, setReason] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset + auto-focus whenever this section mounts / total changes.
  useEffect(() => {
    setTendered(String(Math.ceil(total / 1000) * 1000));
    setAllowPartial(false);
    setReason("");
    setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 60);
  }, [total]);

  const tenderedNum = parseFloat(tendered) || 0;
  const change = tenderedNum - total;
  const isPartial = tenderedNum > 0 && tenderedNum < total;
  const reasonOk = reason.trim().length >= 10;
  const canConfirm = tenderedNum > 0 && (tenderedNum >= total || (allowPartial && hasCustomer && reasonOk));

  const handleConfirm = () => {
    if (canConfirm) onConfirm(tenderedNum, couponCode, isPartial && allowPartial ? reason : undefined);
  };

  // Keyboard: Enter = confirm, Esc = back.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onBack();
      if (e.key === "Enter" && canConfirm && !loading) handleConfirm();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, canConfirm]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={onBack} disabled={loading} className="shrink-0">
          <ArrowLeft size={16} />
        </Button>
        <span className="font-semibold text-base">Thanh toán tiền mặt</span>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Tổng cộng</span>
          <span className="text-xl font-bold text-primary">{fmtVND(total)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <label className="text-muted-foreground shrink-0">Tiền khách đưa</label>
          <Input
            ref={inputRef}
            type="number"
            value={tendered}
            onChange={(e) => setTendered(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
            className="text-right font-mono text-base w-40"
            min={0}
          />
        </div>

        {/* Partial-pay section — only visible when tendered < total */}
        {isPartial && (
          <div className={`rounded-md border px-3 py-2 ${allowPartial && hasCustomer ? "border-amber-300 bg-amber-50" : "border-destructive/30 bg-destructive/5"}`}>
            {hasCustomer ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={allowPartial} onChange={(e) => setAllowPartial(e.target.checked)} />
                  <span className="text-amber-800 font-medium">Trả một phần — ghi nợ {fmtVND(total - tenderedNum)}</span>
                </label>
                {allowPartial && (
                  <div>
                    <label className="block text-xs text-amber-700 mb-1">
                      Lý do ghi nợ (tối thiểu 10 ký tự) <span className="text-destructive">*</span>
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      placeholder="Ví dụ: Khách hàng thân thiết, sẽ trả vào cuối tháng..."
                      className="w-full rounded-md border border-amber-300 bg-white px-2 py-1 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
                    />
                    {reason.trim().length > 0 && !reasonOk && (
                      <p className="text-xs text-destructive mt-0.5">Cần tối thiểu 10 ký tự ({reason.trim().length}/10)</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-destructive">Tiền khách đưa chưa đủ. Chọn khách hàng để ghi nợ phần còn lại.</p>
            )}
          </div>
        )}

        <div className="flex justify-between border-t pt-3">
          <span className="text-muted-foreground">{isPartial && allowPartial && hasCustomer ? "Ghi nợ" : "Tiền thừa"}</span>
          <span className={`text-base font-bold ${change >= 0 ? "text-green-600" : "text-amber-600"}`}>
            {isPartial && allowPartial && hasCustomer ? fmtVND(total - tenderedNum) : fmtVND(Math.max(0, change))}
          </span>
        </div>
      </div>

      {/* Quick-amount + exact buttons */}
      <div className="flex flex-wrap gap-2">
        {QUICK_AMOUNTS.map((amt) => {
          const rounded = amt >= total ? amt : Math.ceil(total / amt) * amt;
          return (
            <Button key={amt} variant="outline" size="sm" onClick={() => setTendered(String(rounded))} className="text-xs">
              {fmtVND(rounded)}
            </Button>
          );
        })}
        <Button variant="outline" size="sm" onClick={() => setTendered(String(total))} className="text-xs">Đủ</Button>
      </div>

      <div className="flex flex-col gap-2 pt-1">
        <Button onClick={handleConfirm} disabled={!canConfirm || loading} className="w-full font-semibold">
          {loading ? "Đang xử lý..." : "Xác nhận (↵)"}
        </Button>
        <Button variant="outline" onClick={onBack} disabled={loading} className="w-full">Quay lại (Esc)</Button>
      </div>
    </div>
  );
}
